/**
 * Single source of truth for practice prompt engineering.
 *
 * This module is intentionally **runtime-agnostic** so it can be imported by:
 * - Supabase Edge Functions (Deno)
 * - The frontend app (Vite/React)
 *
 * Do not import browser-only or Vite-only modules from here.
 */

export type PracticeTheme =
  | 'Team Day'
  | 'Passing Day'
  | 'Running Day'
  | 'Defense Day'
  | 'Special Teams Day'
  | 'Game Day Prep'
  | 'Recovery Day'
  | 'Shooting Day'
  // Baseball-specific themes used in prompts
  | 'Hitting Day'
  | 'Fielding Day'
  | 'Scrimmage Day';

export type TeamLike = {
  name: string;
  sport: string;
  ageLevel: string;
  experienceLevel: string;
  offensiveScheme?: string;
  defensiveScheme?: string;
};

export interface PracticePromptOptions {
  usePositionGroups?: boolean;
  /** Drills the coach explicitly asked for — must appear as distinct drills in the outline JSON. */
  coachMandatoryDrillNames?: string[];
}

const getUniversalPracticeRules = (usePositionGroups: boolean): string => `
UNIVERSAL SEGMENT RULES (APPLY TO ALL SPORTS):
- Warmups are always team-wide. Use exactly one position group: ALL.
- Water breaks and hydration breaks are the same segment type and are always team-wide. Use exactly one position group: ALL.
- Cooldowns are always team-wide and simple. Use exactly one position group: ALL.
- Never split warmups, water breaks, or cooldowns by position.
- ${usePositionGroups
    ? 'For normal drill content, use position groups only when it clearly improves instruction quality; avoid unnecessary over-fragmentation.'
    : 'Position-group drill generation is disabled for this team. Use ALL for every drill segment, including normal drills.'}

QUALITY EXPECTATIONS:
- Build a coach-friendly flow: warmup -> instruction/fundamentals -> active drills -> cooldown.
- Keep activities age-appropriate and sport-appropriate.
- Use practical drills coaches can run with typical youth equipment and staffing.
- Keep transitions natural and avoid repetitive or randomly assembled segments.
- Do not over-split practice into micro-segments.
- Suggested drill steps are options, not a mandatory sequence; coaches should be able to pick one or two and favorite others for later.
- Do not output numbered step names for drill options.
`;

// Base prompt template for practice outline
export const BASE_PRACTICE_OUTLINE_PROMPT = (
  team: TeamLike,
  duration: number,
  focus_areas?: string[],
  theme: PracticeTheme | string = 'Team Day',
  date?: string,
  start_time?: string,
  location?: string,
  options?: PracticePromptOptions
): string => {
  const usePositionGroups = options?.usePositionGroups ?? true;
  const focusAreasText = focus_areas?.length
    ? `\nFocus Areas: ${focus_areas.join(', ')}`
    : '';
  const mandatory = options?.coachMandatoryDrillNames?.filter(Boolean) ?? [];
  const mandatoryBlock =
    mandatory.length > 0
      ? `\n\nCOACH-MANDATED DRILLS (CRITICAL):\n${mandatory.map((n) => `- You MUST include a drill named clearly after: "${n}" (use that exact coaching idea; concise title is fine).\n`).join('')}- These are non-negotiable: do not omit them, merge them into unrelated segments, or replace them with generic filler.`
      : '';

  return `Generate a practice plan outline for a ${team.sport} team with the following structure and constraints. Output MUST be in the specified JSON structure. Do not include markdown or extra explanation.

Team Information:
- Name: ${team.name}
- Sport: ${team.sport}
- Age Level: ${team.ageLevel}
- Experience Level: ${team.experienceLevel}
${team.offensiveScheme ? `- Offensive Scheme: ${team.offensiveScheme}` : 'Balanced'}
${team.defensiveScheme ? `- Defensive Scheme: ${team.defensiveScheme}` : 'Balanced'}

Practice Details:
- Date: ${date || 'YYYY-MM-DD'}
- Start Time: ${start_time || 'HH:MM'}
- Location: ${location || 'string'}
- Duration: ${duration} minutes
- Theme: ${theme}${focusAreasText}${mandatoryBlock}

JSON STRUCTURE REQUIREMENTS:
The response MUST follow this EXACT structure:
{
  "practice": {
    "date": "${date || 'YYYY-MM-DD'}",
    "start_time": "${start_time || 'HH:MM'}",
    "end_time": "HH:MM",
    "location": "${location || 'string'}",
    "theme": "${theme || 'Team Day'}"
  },
  "drills": [
    {
      "name": "string",
      "duration": number,
      "description": "string",
      "focus_area": "string",
      "intensity_level": "string" | null,
      "position_groups": [
        {
          "group_name": "string",
          "stepCount": number
        }
      ]
    }
  ]
}

For each drill, include:
- Name (concise, no unnecessary description)
- Duration in minutes
- Focus Area 
- Position Groups array 

${getUniversalPracticeRules(usePositionGroups)}

Ensure the total duration matches the requested ${duration} minutes.`;
};

// Football-specific practice outline prompt
export const FOOTBALL_PRACTICE_OUTLINE_PROMPT = (
  _team: TeamLike,
  duration: number,
  _focusAreas?: string[],
  theme: PracticeTheme | string = 'Team Day',
  options?: PracticePromptOptions
): string => {
  const usePositionGroups = options?.usePositionGroups ?? true;
  return `
Additional Football Requirements:
IMPORTANT: Drills should be USA Football Certified.
1. Break down the practice into these EXACT segments. Ensure the total duration of all drills equals ${duration} minutes. Adjust number and length of drills accordingly.
    a. Dynamic Warmup (10 min) - Team-wide (ALL). This should consist of a total of 8-10 warmup movements. It should be a mix of dynamic stretches and plyometric exercises. Add pushups, situps, up-downs, and other bodyweight exercises to the mix.
    b. Aggression Drills (10min) - Intensity level: "Game speed". This should be full contact, and really get the players engaged. Some examples of this are Midnight Alley, LSU Aggression Drill, Machine Gun and more. 
    c. Water Break (3 min) - Team-wide (ALL). Intensity level: null (water breaks do not have an intensity level)
    d. Tackling Circuit (15 min) - Intensity level: "Game speed". Form, Angle, Gator Roll (ALL groups). These should be USA Football Certified, focusing on proper tackling technique. However, these drills should also be aggression drills.
    e. Water Break (3 min) - Team-wide (ALL). Intensity level: null (water breaks do not have an intensity level)
    f. Conditioning/Speed/Agility (8 min) - Intensity level: "Game speed". 2 unique drills. These should be high-intensity drills that are USA Football Certified.
    g. Individual Fundamentals - First (12 min) - Intensity level: "Technique focused" (default). Position groups to be determined by theme - EXACTLY 3 unique drills per group
    h. Water Break (3 min) - Team-wide (ALL). Intensity level: null (water breaks do not have an intensity level)
    i. Individual Fundamentals - Second (12 min) - Intensity level: "Technique focused" (default). Position groups to be determined by theme - EXACTLY 3 unique drills per group
    j. Special Teams (10 min) - Intensity level: "Technique focused" (default). Focus to be determined by theme
    k. Water Break (3 min) - Team-wide (ALL). Intensity level: null (water breaks do not have an intensity level)
    l. Team Installation (20 min) - Intensity level: "Walk-through" or "Technique focused". Focus to be determined by theme. Position groups should be ALL.
    m. Team Competition (9 min) - Intensity level: "Game speed". High-energy team game such as ultimate football, tug of war, or other team games.

Include a brief Cooldown segment at the end when time allows. Cooldown must be team-wide (ALL) and simple.

2. Position Group Requirements:
   - When ${theme} is "Offense Day": Include drills for these EXACT position groups: QB, WR, OL, RB for both Individual Fundamentals sessions
   - When ${theme} is "Defense Day": Include drills for these EXACT position groups: DL, LB, DB for both Individual Fundamentals sessions
   - When ${theme} is "Team Day": Include drills for both offensive and defensive position groups split into two sessions. Session 1 will be the offensive drills and Session 2 will be the defensive drills.
 
3. Special Teams Focus by Theme:
   - When ${theme} is "Offense Day": Focus on Kick Returns, Punts, Field Goals, Extra Points
   - When ${theme} is "Defense Day": Focus on Kickoffs, Punt Returns, Punt Blocks, Punt Coverage
   - When ${theme} is "Team Day": Include all special teams aspects with equal focus

4. Team Installation Focus by Theme:
   - When ${theme} is "Offense Day": Focus on offensive schemes, end with Red Zone or Two-Minute Drill
   - When ${theme} is "Defense Day": Focus on defensive schemes, end with goal line stand or 2pt conversion defense
   - When ${theme} is "Team Day": Focus on offensive and defensive schemes, end with goal line stand or 2pt conversion defense

5. Each drill must be structured as:
   - A main drill category (e.g., "Dynamic Stretches", "Tackling Circuit")
   - Position groups that participate in the drill (e.g., "ALL", "QB", "WR", "OL", "RB", "DL", "LB", "DB")
   - Steps that are the individual exercises within the drill (e.g., "Leg Swings", "Arm Circles" for Dynamic Stretches)
6. Position-group mode for this team:
   - ${usePositionGroups ? 'Enabled: use position-specific groups for applicable skill sections only.' : 'Disabled: use ALL for every drill in the practice.'}`;
};

// Drill details prompt template
export const DRILL_DETAILS_PROMPT = (
  drillName: string,
  positionGroups: string[],
  drillDuration: number,
  drillStepsMinimum: number,
  team: TeamLike
): string => {
  const drillNameLower = drillName.toLowerCase();
  const isWarmup = drillNameLower.includes('warmup') || drillNameLower.includes('warm-up') || drillNameLower.includes('dynamic warm');
  const isWaterBreak = drillNameLower.includes('water') || drillNameLower.includes('hydration');
  const isCooldown = drillNameLower.includes('cooldown') || drillNameLower.includes('cool down');
  return `Generate detailed drill information for the following drill:
  
  Drill Name: ${drillName}
  Duration: ${drillDuration} minutes
  Exact Suggested Steps Per Position Group: ${drillStepsMinimum}
  Position Groups: ${positionGroups.join(', ')}
  Sport: ${team.sport}
  Age Level: ${team.ageLevel}
  Experience Level: ${team.experienceLevel}
  Return a JSON object with the following structure:
  {
    "drills": [
      {
        "name": "string",
        "duration": number,
        "description": "string",
        "focus_area": "string",
        "intensity_level": "string" | null,
        "position_groups": [
          {
            "group_name": "string",
            "steps": [
              {
                "name": "string",
                "setup": "string",
                "execution": "string",
                "coachingPoints": ["string", "string", "string"],
                "equipment": "string",
                "video_url": "string"
              }
            ]
          }
        ]
      }
    ]
  }

  INTENSITY LEVEL REQUIREMENTS (CRITICAL - MUST BE INCLUDED):
  - The "intensity_level" field MUST ALWAYS be included in the response for EVERY drill
  - The "intensity_level" field MUST be one of the following values: "Walk-through", "Technique focused", "Game speed", or null
  - DEFAULT: "Technique focused" (use this for most drills unless otherwise specified)
  - Use "Game speed" for high-intensity, full-contact, or competitive drills (e.g., Aggression Drills, Conditioning, Team Competition)
  - Use "Walk-through" for low-intensity drills focused on form and technique without physical exertion (e.g., slow-motion technique work, mental reps)
  - Use null ONLY for water breaks and hydration periods (drills with names containing "water", "hydration", or "break" in the context of rest)
  - These values MUST match exactly: "Walk-through", "Technique focused", "Game speed" (case-sensitive)
  - DO NOT omit the intensity_level field - it is required for all drills

  CRITICAL: Each drill must have EXACTLY ${drillStepsMinimum} suggested steps per position group (no more, no less), except warmup/cooldown/water break segments which can be simpler.
  - For each position group:
    - Provide specific setup instructions for that group
    - Include detailed execution steps
    - List 3-5 specific coaching points
    - Specify required equipment
    - If postion group is ALL, do NOT separate into individual position groups. Just list ALL groups.
  - Each drill should be related to the theme of the practice.
   
  IMPORTANT: Structure the drill as a main category with steps being the individual exercises.
  QUALITY CONTROL FOR STEP COUNT VS TIME:
  - Keep suggested step count realistic for the drill duration. Do not overload short segments with too many unique steps.
  - For short drill segments, steps should be concise options coaches can pick from, not a mandatory full checklist.
  - Do not number steps (no "1.", "2.", "Step 1", etc.).
  - Do not prefix step names with "Option" or "Alternative Option"; use only the actual drill-step name.
  - Phrase steps as selectable options so coaches can choose one or two now and save others for another day.
  SEGMENT-SPECIFIC RULES:
  - Warmups, water breaks, and cooldowns must use only ALL as the position group.
  - Warmups are team-wide and can use flexible structure; avoid over-constraining warmup into rigid micro-steps.
  - Water breaks and cooldowns should remain simple and coach-friendly.
  - Current segment classification: warmup=${isWarmup}, waterBreak=${isWaterBreak}, cooldown=${isCooldown}
  
  `;
};

// Basketball-specific prompt template
export const BASKETBALL_PROMPT_TEMPLATE = (
  _team: TeamLike,
  _duration: number,
  _focusAreas?: string[],
  _theme: PracticeTheme | string = 'Team Day',
  options?: PracticePromptOptions
): string => {
  const usePositionGroups = options?.usePositionGroups ?? true;
  
  return `

Additional Basketball Requirements:
- Warmups, water breaks, and cooldowns must always be team-wide (ALL).
- Include hydration breaks at logical intervals for the total duration.
- Include a simple team-wide cooldown at the end when time allows.
- ${usePositionGroups ? 'For skill sections, include position-specific drills for:' : 'Position-group mode is disabled: use ALL for every drill and avoid position-specific splits.'}
${usePositionGroups ? `  * Point Guards
  * Shooting Guards
  * Small Forwards
  * Power Forwards
  * Centers` : ''}

- For shooting-focused practices:
  * Include form shooting
  * Include catch-and-shoot drills
  * Include off-the-dribble shooting

- For defense-focused practices:
  * Include on-ball defense
  * Include help defense
  * Include rebounding techniques

- For team-focused practices:
  * Include fast break drills
  * Include half-court sets
  * Include defensive rotations`;
};

// Baseball-specific prompt template
export const BASEBALL_PROMPT_TEMPLATE = (
  _team: TeamLike,
  duration: number,
  _focusAreas?: string[],
  theme: PracticeTheme | string = 'Team Day',
  options?: PracticePromptOptions
): string => {
  const usePositionGroups = options?.usePositionGroups ?? true;
  
  // Hitting Day specific requirements
  const themeStr = theme as string;
  if (themeStr === 'Hitting Day') {
    const hittingTime = duration - 25; // Reserve 10 min warmup, 10 min pitchers/catchers, 5 min water break
    return `
Additional Baseball Requirements for HITTING DAY:
CRITICAL: The entire practice must be focused exclusively on HITTING and BATTING PRACTICE. Do NOT include fielding drills, team defense, or any non-hitting activities (except pitcher/catcher time).

1. Practice Structure (${duration} minutes total):
   a. Dynamic Warmup (10 minutes) - Intensity level: "Technique focused". FULL TEAM together - all players do the same warmup routine. Include arm circles, lunges, high knees, hip rotations, and light stretching focused on preparing the body for hitting. Position groups: ALL
   
   b. ROTATING HITTING STATIONS - This is the MAIN FOCUS of the practice (${hittingTime} minutes). Divide ALL players (except pitchers/catchers) into 3 groups and rotate them through 3 stations simultaneously. Players should NEVER be sitting around waiting:
      * Station 1: Tee Work (${Math.floor(hittingTime / 3)} minutes) - Intensity level: "Technique focused". Multiple tee stations, focus on swing mechanics, contact point, and body position. Position groups: ALL (non-pitchers/non-catchers)
      * Station 2: Soft Toss (${Math.floor(hittingTime / 3)} minutes) - Intensity level: "Technique focused". Coaches/parents soft tossing, focus on timing and barrel contact. Position groups: ALL (non-pitchers/non-catchers)
      * Station 3: Live BP or Batting Cages (${Math.floor(hittingTime / 3)} minutes) - Intensity level: "Game speed". Live batting practice, batting cages, or pitching machines. Position groups: ALL (non-pitchers/non-catchers)
   
   c. Water Break (5 minutes) - Team-wide (ALL). Intensity level: null.
   
   d. Pitchers & Catchers Time (10 minutes) - Intensity level: "Technique focused". Pitchers work on mechanics, pitch development. Catchers work on receiving, blocking, framing. Position groups: Pitchers, Catchers ONLY

2. ROTATION REQUIREMENTS:
   - Divide ALL position players (non-pitchers/non-catchers) into exactly 3 groups
   - Each group rotates through ALL 3 hitting stations
   - Timing MUST be coordinated so players are ALWAYS actively hitting
   - NO player should be sitting or waiting between rotations
   - Use multiple batting cages, tee stations, and soft toss areas simultaneously

3. Focus Areas for ALL hitting drills:
   - Proper batting stance and setup
   - Body position and weight transfer
   - Swing mechanics and bat path
   - Contact point and barrel control
   - Timing and pitch recognition
   - Two-strike approach and plate discipline

4. Equipment Needed:
   - Multiple batting tees
   - Bats and helmets for all players
   - Batting cages or pitching machines (if available)
   - Baseballs/softballs (plenty for continuous work)
   - Soft toss nets/barriers
   - Hitting mats or turf areas

5. Position Groups:
   - Warmup: ALL (full team together, no splitting)
   - Hitting Stations: ALL position groups participate (non-pitchers/non-catchers rotate through stations)
   - Pitchers & Catchers: Separate time for pitchers and catchers only
   - NO position-specific hitting drills (everyone hits the same way)

IMPORTANT: 
- The total duration MUST match ${duration} minutes exactly
- EVERY drill must be hitting-focused (except pitcher/catcher time)
- Warmups are ALWAYS full team together - do NOT split into groups or positions
- Players must be actively engaged at ALL times - no waiting around
- ${usePositionGroups ? 'Position groups may be used for applicable skill sections only.' : 'Position-group mode is disabled for this team. Use ALL for all drills.'}`;
  }

  // Team Day specific requirements (NO batting practice)
  if (themeStr === 'Team Day') {
    return `
Additional Baseball Requirements for TEAM DAY:
CRITICAL: Team Day practices should focus on fielding, throwing, team defense, and base running. Do NOT include batting practice - that is reserved for Hitting Day practices only.

1. Practice Structure (${duration} minutes total):
   a. Dynamic Warmup (10 minutes) - Team-wide (ALL). Intensity level: "Technique focused". Include arm circles, lunges, high knees, and light throwing
   b. Team Throwing Program (10 minutes) - Intensity level: "Technique focused". Progressive throwing distances, accuracy, long toss, proper mechanics
   c. Water Break #1 (5 minutes) - Team-wide (ALL). Intensity level: null
   
   d. Position-Specific Fundamentals (15 minutes as a whole block. Each position group gets 15 minutes simultaneously) - Intensity level: "Technique focused". Split into position groups with EXACTLY 3 drills for EACH group:
      * Infielders: Focus on ground ball work, double plays, and throwing mechanics
      * Outfielders: Focus on fly balls, cutoffs, and throws to bases
      * Pitchers: Focus on mechanics, pickoffs, fielding position, and pitch development
      * Catchers: Focus on receiving, blocking, framing, and throwing to bases
   
   e. Water Break #2 (5 minutes) - Team-wide (ALL). Intensity level: null
   
   f. Team Defense (${Math.floor((duration - 50) * 0.4)} minutes) - Intensity level: "Game speed". Focus on game situations, cutoffs, relays, and coordinated defensive plays
   
   g. Base Running (${Math.floor((duration - 50) * 0.3)} minutes) - Intensity level: "Game speed" or "Technique focused". Focus on base running techniques, leads, steals, and base running strategies
   
   h. Team Competition/Scrimmage (${Math.floor((duration - 50) * 0.3)} minutes) - Intensity level: "Game speed". Situational scrimmages, defensive situations, or skill competitions

2. Focus Areas (NO HITTING):
   - Fielding and defensive positioning
   - Throwing accuracy and mechanics
   - Team defense and communication
   - Base running and base stealing
   - Game situations and strategy
   - Pitcher/catcher coordination

3. Position-specific requirements (15 minutes each, all happening simultaneously):
   - Infielders: Ground balls, double plays, cutoffs, throwing accuracy, defensive positioning
   - Outfielders: Fly balls, throwing to cutoffs, communication, defensive positioning
   - Pitchers: Mechanics, pickoffs, fielding position, pitch development, holding runners
   - Catchers: Receiving, blocking, framing, throwing to bases, game calling

4. Water breaks MUST be 5 minutes each and included in total duration
5. Every drill should focus on fundamental development appropriate for age level and experience
6. Drills should be specific, detail-oriented, and use appropriate equipment

IMPORTANT: 
- The total duration MUST match ${duration} minutes exactly
- Do NOT include batting practice or hitting drills
- Focus on realistic, achievable drills for a ${duration}-minute practice
- ${usePositionGroups ? 'Position groups may be used for applicable skill sections only.' : 'Position-group mode is disabled for this team. Use ALL for all drills.'}`;
  }

  // Fielding Day and other themes
  return `
Additional Baseball Requirements:
1. Break down the practice into segments with EXACTLY ${duration} minutes total duration
2. Focus on activities appropriate for the "${theme}" theme
3. Structure the practice with these components:
   a. Dynamic Warmup (10 minutes) - Team-wide (ALL). Intensity level: "Technique focused". Include arm circles, lunges, high knees, and light throwing
   b. Team Throwing Program (10 minutes) - Intensity level: "Technique focused". Progressive throwing distances, accuracy, long toss
   c. Water Break #1 (5 minutes) - Team-wide (ALL). Intensity level: null
   d. Theme-Specific Drills (${duration - 30} minutes) - Focus on the practice theme
   e. Water Break #2 (5 minutes) - Team-wide (ALL). Intensity level: null (if time permits)
   f. Competition/Scrimmage (varies) - Intensity level: "Game speed"

4. For Fielding Day theme: Focus exclusively on fielding, defensive positioning, and throwing
5. For Scrimmage Day theme: Focus on game-like situations and competition
6. Water breaks MUST be included in the total duration
7. Every drill should be focused on age level and experience level fundamental development
8. Every drill should be extremely specific and detail-oriented
9. Include a brief, simple team-wide cooldown (ALL) at the end when time permits.
10. ${usePositionGroups ? 'Use position groups only where truly useful for skill development.' : 'Position-group mode is disabled for this team. Use ALL for all drills.'}

IMPORTANT: The total duration of the practice MUST match the requested ${duration} minutes exactly.`;
};

// Helper function to get the appropriate sport-specific prompt
export const getSportSpecificPrompt = (
  team: TeamLike,
  duration: number,
  focusAreas?: string[],
  theme?: PracticeTheme | string,
  options?: PracticePromptOptions
): string => {
  switch (team.sport.toLowerCase()) {
    case 'football':
      return FOOTBALL_PRACTICE_OUTLINE_PROMPT(team, duration, focusAreas, theme, options);
    case 'basketball':
      return BASKETBALL_PROMPT_TEMPLATE(team, duration, focusAreas, theme, options);
    case 'baseball':
    // Softball shares baseball's structure, themes and stat model, so it uses
    // the same template. (FlagFootball deliberately does NOT fall through to
    // the football template — that one prescribes tackling circuits, aggression
    // drills and full contact, which must never be generated for a non-contact
    // sport. It uses the generic default below until it gets its own template.)
    case 'softball':
      return BASEBALL_PROMPT_TEMPLATE(team, duration, focusAreas, theme, options);
    default:
      return `Additional ${team.sport} Requirements:
- Keep the structure realistic for ${team.sport} youth coaching.
- Apply warmup, hydration break, and cooldown as team-wide (ALL) segments.
- Use position groups only when clearly useful for instruction quality and only if position-group mode is enabled.
- Include at least one hydration break for standard practice lengths.
- End with a brief team-wide cooldown when time allows.`;
  }
};

// Helper function to get the complete practice outline prompt
export const getPracticeOutlinePrompt = (
  team: TeamLike,
  duration: number,
  focus_areas?: string[],
  theme?: PracticeTheme | string,
  date?: string,
  start_time?: string,
  location?: string,
  options?: PracticePromptOptions
): string => {
  const basePrompt = BASE_PRACTICE_OUTLINE_PROMPT(team, duration, focus_areas, theme, date, start_time, location, options);
  const sportSpecificPrompt = getSportSpecificPrompt(team, duration, focus_areas, theme, options);
  
  return `${basePrompt}\n\n${sportSpecificPrompt}`;
};

